import React, { useState, useEffect } from "react";
import axios from "axios";
import { getToken } from "utils/getToken";
import { ToastContainer } from "react-toastify";
import EditSlotModal from "./EditSlotModal";
import SyncLoader from "components/Loaders/SyncLoader";
import {
  convertTimeTo12HourReadbleFormat,
  formatToDate,
} from "utils/formatTime";
import {
  dangerBtn,
  emptyState,
  focusRing,
  ghostBtn,
  pageKicker,
  pageTitle,
  primaryBtn,
  select,
  table,
  tablePanel,
  td,
  th,
  trHover,
} from "components/ui/instrument";

const headers = {
  "Content-Type": "application/json",
  Authorization: `Token ${getToken()}`,
};

interface EditSlotModalState {
  open: boolean;
  slotId?: any;
  type?: string;
  feature?: string;
  slotDetails?: any;
  doctorId?: any;
  slotType?: any;
}

const DoctorsAppointments = () => {
  const [doctors, setDoctors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [inactiveSlots, setInactiveSlots] = useState<any[]>([]);
  const [scheduleSlots, setScheduleSlots] = useState<any[]>([]);
  const [openEditSlotModal, setOpenEditSlotModal] = useState<EditSlotModalState>(
    {
      open: false,
    }
  );
  const [activeTab, setActiveTab] = useState("weeklySlots");
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const consultationTypeOption = [
    "Choose type of Consultation",
    "lactation",
    "growth",
    "nutrition",
  ];
  const [selectedConsultation, setSelectedConsultation] = useState(
    consultationTypeOption[0]
  );
  const [showErrorMessage, setShowErrorMessage] = useState(true);

  useEffect(() => {
    if (selectedConsultation === consultationTypeOption[0]) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
      getAvailableDoctors();
      setSelectedDoctor(null); // Reset doctor selection when consultation type changes
      setAvailableSlots([]);
      setInactiveSlots([]);
      setScheduleSlots([]);
    }
  }, [selectedConsultation]);

  useEffect(() => {
    if (selectedDoctor && selectedDoctor.id) {
      getSlotsByDocId(selectedDoctor.id, "active");
      getSlotsByDocId(selectedDoctor.id, "inactive");
      getScheduleSlotsByDocId(selectedDoctor.id);
    } else {
      setAvailableSlots([]);
      setInactiveSlots([]);
      setScheduleSlots([]);
    }
  }, [selectedDoctor]);

  useEffect(() => {
    if (isDataUpdated) {
      if (selectedDoctor && selectedDoctor.id) {
        getSlotsByDocId(selectedDoctor.id, "active");
        getSlotsByDocId(selectedDoctor.id, "inactive");
        getScheduleSlotsByDocId(selectedDoctor.id);
      }
      setIsDataUpdated(false);
    }
  }, [isDataUpdated]);

  const handleDoctorSelect = (doctor: any) => {
    setSelectedDoctor(doctor);
  };

  const handleRefresh = () => {
    if (selectedDoctor && selectedDoctor.id) {
      getSlotsByDocId(selectedDoctor.id);
      getScheduleSlotsByDocId(selectedDoctor.id);
    }
  };

  const getAvailableDoctors = async () => {
    try {
      setIsLoading(true);
      const doctorsResponse = await axios.get(
        process.env.REACT_APP_API_URL +
          `doctors/doctors/all/?category=${selectedConsultation}&list_type=all`,
        {
          headers,
        }
      );

      if (doctorsResponse.status === 200) {
        if (doctorsResponse.data?.length > 0) {
          setDoctors(doctorsResponse.data);
        } else {
          setDoctors([]);
        }
      }
    } catch (err) {
      setDoctors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSlotsByDocId = async (doctorId: any, slotType?: string) => {
    try {
      setIsLoading(true);
      const doctorsResponse = await axios.post(
        process.env.REACT_APP_API_URL + "doctors/weekly-availability/",
        {
          slot_type: selectedConsultation,
          doctor_ids: [doctorId],
        },
        {
          headers,
        }
      );

      if (doctorsResponse.status === 200) {
        const slots = doctorsResponse.data;
        if (slotType === "active") {
          setAvailableSlots(
            slots.filter((slot: any) => slot.status === "active")
          );
        } else if (slotType === "inactive") {
          setInactiveSlots(
            slots.filter((slot: any) => slot.status === "inactive")
          );
        }
      }
    } catch (err) {
      if (slotType === "active") {
        setAvailableSlots([]);
      } else if (slotType === "inactive") {
        setInactiveSlots([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getScheduleSlotsByDocId = async (doctorId: any) => {
    try {
      setIsLoading(true);
      const doctorsResponse = await axios.post(
        process.env.REACT_APP_API_URL + "doctors/doctor-appointment-slots/",
        {
          doctor_ids: [doctorId],
          status: ["free", "booked"],
          slot_type: selectedConsultation
        },
        {
          headers,
        }
      );

      if (doctorsResponse.status === 200) {
        setScheduleSlots(doctorsResponse.data);
      }
    } catch (err) {
      setScheduleSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto sm:w-full">
      <EditSlotModal
        openEditSlotModal={openEditSlotModal}
        setOpenEditSlotModal={setOpenEditSlotModal}
        setIsDataUpdated={setIsDataUpdated}
      />

      <div className="py-6 mb-5">
        {/* Header */}
        <div className="flex items-center justify-between px-2 mr-1 md:px-0">
          <div className="flex gap-2">
            <select
              id="consultationType"
              value={selectedConsultation}
              onChange={(e) => setSelectedConsultation(e.target.value)}
              className={`${select} w-full`}
            >
              {consultationTypeOption.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            {selectedConsultation !== consultationTypeOption[0] && (
              <select
                className={select}
                onChange={(e) =>
                  handleDoctorSelect(
                    doctors.find(
                      (doctor) => doctor.id === parseInt(e.target.value)
                    )
                  )
                }
              >
                <option value="">Select a doctor</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="text-center">
            <p className={pageKicker}>Babynama Clinic-OS</p>
            <h1 className={pageTitle}>
              Manage Doctor's Schedule
            </h1>
          </div>

          <div>
            <button
              disabled={isLoading && !selectedDoctor}
              className={ghostBtn}
              onClick={handleRefresh}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="border-b border-clinic-200 dark:border-navy-700">
        <ul className="flex flex-wrap justify-center gap-4 -mb-px text-sm font-medium text-center text-clinic-500 dark:text-gray-400">
          <li className="me-2">
            <button
              onClick={() => setActiveTab("weeklySlots")}
              className={`inline-flex ${
                activeTab === "weeklySlots"
                  ? "border-b-2 border-action-600 font-semibold text-action-600 dark:text-action-300"
                  : "border-b-2 border-transparent text-clinic-500 hover:border-clinic-300 hover:text-clinic-700 dark:text-gray-400 dark:hover:text-gray-200"
              } group items-center justify-center rounded-t-lg p-4 transition-colors motion-reduce:transition-none ${focusRing}`}
            >
              Available Weekly Slots
            </button>
          </li>

          <li className="me-2">
            <button
              onClick={() => setActiveTab("scheduleSlots")}
              className={`inline-flex ${
                activeTab === "scheduleSlots"
                  ? "border-b-2 border-action-600 font-semibold text-action-600 dark:text-action-300"
                  : "border-b-2 border-transparent text-clinic-500 hover:border-clinic-300 hover:text-clinic-700 dark:text-gray-400 dark:hover:text-gray-200"
              } group items-center justify-center rounded-t-lg p-4 transition-colors motion-reduce:transition-none ${focusRing}`}
            >
              Scheduled Slots
            </button>
          </li>
        </ul>
      </div>

      {showErrorMessage ? (
        <div className="flex h-[70vh] items-center justify-center text-sm text-clinic-500 dark:text-gray-400">
          Please choose a type of consultation.
        </div>
      ) : !selectedDoctor ? (
        <div className="flex h-[70vh] items-center justify-center text-sm text-clinic-500 dark:text-gray-400">
          Please select a doctor.
        </div>
      ) : isLoading ? (
        <div className="flex h-[70vh] items-center justify-center">
          <SyncLoader color={"#0e7490"} size={10} />
        </div>
      ) : activeTab === "weeklySlots" ? (
        <>
          <div className="mt-5">
            <div className="flex items-center justify-between px-4">
              <h2 className="text-base font-bold tracking-tight text-clinic-900 dark:text-white md:text-lg">
                {selectedDoctor?.name} Active Slots
              </h2>

              {selectedDoctor && (
                <button
                  onClick={() => {
                    setOpenEditSlotModal({
                      open: true,
                      slotId: null,
                      type: "weeklySlot",
                      feature: "add",
                      slotDetails: null,
                      doctorId: selectedDoctor.id,
                      slotType: selectedConsultation,
                    });
                  }}
                  className={primaryBtn}
                >
                  Add Weekly Slot
                </button>
              )}
            </div>

            {availableSlots.length > 0 ? (
              <div className="flow-root px-4 mt-5">
                <ul className="divide-y divide-clinic-200 dark:divide-navy-700">
                  {availableSlots.map((slot) => (
                    <li className="py-3 sm:py-4" key={slot.id}>
                      <div className="flex items-center">
                        <div className="w-1 h-16 bg-clinic-300 rounded-md dark:bg-navy-600"></div>
                        <div className="flex-1 min-w-0 ms-4">
                          <p className="text-base font-medium text-clinic-900 truncate dark:text-white">
                            Day - {slot.day}
                          </p>
                          <p className="text-sm text-clinic-700 truncate tabular-nums dark:text-gray-400">
                            Available -{" "}
                            {convertTimeTo12HourReadbleFormat(slot?.time_from)}{" "}
                            - {convertTimeTo12HourReadbleFormat(slot?.time_to)}
                          </p>
                          <p className="text-sm text-clinic-700 truncate dark:text-gray-400">
                            Status -{" "}
                            <span
                              className={`${
                                slot?.status === "active"
                                  ? `font-medium text-clinic-700 dark:text-gray-200`
                                  : `text-clinic-500 dark:text-gray-400`
                              }`}
                            >
                              {slot.status}
                            </span>
                          </p>
                        </div>

                        <div className="">
                          <button
                            onClick={() => {
                              setOpenEditSlotModal({
                                open: true,
                                slotId: slot.id,
                                type: "weeklySlot",
                                feature: "edit",
                                slotDetails: slot,
                                doctorId: null,
                                slotType: selectedConsultation,
                              });
                            }}
                            className={ghostBtn}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className={`${emptyState} my-4`}>
                No available slots found!
              </div>
            )}
          </div>

          {inactiveSlots.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between px-4">
                <h2 className="text-base font-bold tracking-tight text-clinic-900 dark:text-white md:text-lg">
                  {selectedDoctor?.name} Inactive Slots
                </h2>
              </div>

              <div className="flow-root px-4 mt-5">
                <ul className="divide-y divide-clinic-200 dark:divide-navy-700">
                  {inactiveSlots.map((slot) => (
                    <li className="py-3 sm:py-4" key={slot.id}>
                      <div className="flex items-center">
                        <div className="w-1 h-16 bg-clinic-300 rounded-md dark:bg-navy-600"></div>
                        <div className="flex-1 min-w-0 ms-4">
                          <p className="text-base font-medium text-clinic-900 truncate dark:text-white">
                            Day - {slot.day}
                          </p>
                          <p className="text-sm text-clinic-700 truncate tabular-nums dark:text-gray-400">
                            Available -{" "}
                            {convertTimeTo12HourReadbleFormat(slot?.time_from)}{" "}
                            - {convertTimeTo12HourReadbleFormat(slot?.time_to)}
                          </p>
                          <p className="text-sm text-clinic-700 truncate dark:text-gray-400">
                            Status -{" "}
                            <span
                              className={`${
                                slot?.status === "active"
                                  ? `font-medium text-clinic-700 dark:text-gray-200`
                                  : `text-clinic-500 dark:text-gray-400`
                              }`}
                            >
                              {slot.status}
                            </span>
                          </p>
                        </div>

                        <div className="">
                          <button
                            onClick={() => {
                              setOpenEditSlotModal({
                                open: true,
                                slotId: slot.id,
                                type: "weeklySlot",
                                feature: "edit",
                                slotDetails: slot,
                                doctorId: null,
                                slotType: selectedConsultation,
                              });
                            }}
                            className={ghostBtn}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="px-4 mt-5">
          <div className="flex items-center justify-between px-4">
            <h2 className="text-base font-bold tracking-tight text-clinic-900 dark:text-white md:text-lg">
              {selectedDoctor?.name} Schedule Slots
            </h2>

            {selectedDoctor && (
              <button
                onClick={() => {
                  setOpenEditSlotModal({
                    open: true,
                    slotId: null,
                    type: "scheduleSlot",
                    feature: "add",
                    slotDetails: null,
                    doctorId: selectedDoctor.id,
                    slotType: selectedConsultation,
                  });
                }}
                className={primaryBtn}
              >
                Add Slot
              </button>
            )}
          </div>

          <div className={`${tablePanel} mt-5 min-h-[60vh] max-w-full overflow-y-auto`}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Date</th>
                  <th className={th}>User</th>
                  <th className={th}>Timing</th>
                  <th className={th}>Status</th>
                  <th className={th}>Edit</th>
                </tr>
              </thead>
              <tbody>
                {scheduleSlots.map((slot, rowIndex) => (
                  <tr key={rowIndex} className={trHover}>
                    <td className={`${td} min-w-[150px] tabular-nums`}>
                      {formatToDate(slot.date)}
                    </td>
                    <td className={`${td} min-w-[150px]`}>
                      <div className="flex flex-col items-center gap-2">
                        {slot?.user_name && (
                          <p>User Name - {slot?.user_name}</p>
                        )}
                        {slot?.phone_number && (
                          <p className="tabular-nums">Phone Number - {slot?.phone_number}</p>
                        )}
                      </div>
                    </td>
                    <td className={`${td} min-w-[200px] tabular-nums`}>
                      {convertTimeTo12HourReadbleFormat(slot?.time_from)} -{" "}
                      {convertTimeTo12HourReadbleFormat(slot?.time_to)}
                    </td>
                    <td className={`${td} min-w-[200px]`}>
                      <span
                        className={`${
                          slot?.status === "free"
                            ? `text-clinic-500 dark:text-gray-400`
                            : `font-medium text-clinic-700 dark:text-gray-200`
                        }`}
                      >
                        {slot.status}
                      </span>
                    </td>
                    <td className={`${td} flex min-w-[150px] items-center justify-center`}>
                      <button
                        disabled={slot.status === "booked"}
                        onClick={() => {
                          setOpenEditSlotModal({
                            open: true,
                            slotId: slot.id,
                            type: "scheduleSlot",
                            feature: "edit",
                            slotDetails: {
                              ...slot,
                              doctorName: selectedDoctor?.name,
                            },
                            doctorId: null,
                            slotType: selectedConsultation,
                          });
                        }}
                        className={dangerBtn}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default DoctorsAppointments;
